import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Award, Search } from "lucide-react";

import { ApiClientError } from "../api/client";
import { useApi } from "../api/useApi";
import type { EtfCategoryComparison, EtfCategoryMember } from "../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

function percent(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function ratio(value: number | null | undefined): string {
  return value == null ? "—" : value.toFixed(2);
}

function assets(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function expense(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${(value * 100).toFixed(value * 100 < 0.1 ? 3 : 2)}%`;
}

function leaderLabels(result: EtfCategoryComparison, symbol: string): string[] {
  const labels: string[] = [];
  if (result.leaders.one_year_return === symbol) labels.push("1Y return");
  if (result.leaders.three_year_return === symbol) labels.push("3Y return");
  if (result.leaders.sharpe === symbol) labels.push("Sharpe");
  if (result.leaders.lowest_volatility === symbol) labels.push("Low volatility");
  if (result.leaders.best_drawdown === symbol) labels.push("Drawdown");
  return labels;
}

function MetricCell({ value, leader }: { value: string; leader: boolean }) {
  return (
    <TableCell className={cn("text-right tabular-nums", leader && "font-semibold text-success")}>
      {value}
    </TableCell>
  );
}

function PeerRow({ result, member }: { result: EtfCategoryComparison; member: EtfCategoryMember }) {
  const selected = member.symbol === result.symbol;
  const labels = leaderLabels(result, member.symbol);
  return (
    <TableRow data-testid={`category-member-${member.symbol}`} className={selected ? "bg-primary/5" : undefined}>
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold">{member.symbol}</span>
              {selected && <Badge variant="secondary">Selected</Badge>}
              {member.exposure && (
                <Badge variant="outline">
                  {member.exposure.target_multiple}× {member.exposure.reset_period} {member.exposure.direction}
                </Badge>
              )}
            </div>
            {member.name !== member.symbol && (
              <div className="max-w-52 truncate text-xs text-muted-foreground" title={member.name}>
                {member.name}
              </div>
            )}
          </div>
        </div>
      </TableCell>
      <MetricCell value={assets(member.net_assets)} leader={false} />
      <MetricCell value={expense(member.expense_ratio)} leader={false} />
      <TableCell>
        <div className="flex max-w-44 flex-wrap gap-1">
          {labels.length ? labels.map((label) => (
            <Badge key={label} variant="outline" className="gap-1 text-[10px]">
              <Award className="size-3" />{label}
            </Badge>
          )) : <span className="text-muted-foreground">—</span>}
        </div>
      </TableCell>
      <MetricCell value={percent(member.one_year_return)} leader={result.leaders.one_year_return === member.symbol} />
      <MetricCell value={percent(member.three_year_return)} leader={result.leaders.three_year_return === member.symbol} />
      <MetricCell value={percent(member.five_year_return)} leader={false} />
      <MetricCell value={ratio(member.sharpe)} leader={result.leaders.sharpe === member.symbol} />
      <MetricCell value={percent(member.volatility)} leader={result.leaders.lowest_volatility === member.symbol} />
      <MetricCell value={percent(member.max_drawdown)} leader={result.leaders.best_drawdown === member.symbol} />
      <TableCell className="text-right">
        {selected ? "—" : (
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/compare?a=${encodeURIComponent(result.symbol)}&b=${encodeURIComponent(member.symbol)}`}>
              Compare
            </Link>
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

export function Categories() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const initial = params.get("symbol")?.toUpperCase() || "SPY";
  const [symbol, setSymbol] = useState(initial);
  const [result, setResult] = useState<EtfCategoryComparison | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const load = useCallback(async (raw: string) => {
    const normalized = raw.trim().toUpperCase();
    if (!normalized) {
      setStatus("Enter an ETF symbol.");
      return;
    }
    setLoading(true);
    setStatus("");
    try {
      const next = await api.compareEtfCategory(normalized);
      setResult(next);
      setSymbol(normalized);
      setParams({ symbol: normalized }, { replace: true });
    } catch (error) {
      setResult(null);
      setStatus(
        error instanceof ApiClientError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Could not load that ETF category.",
      );
    } finally {
      setLoading(false);
    }
  }, [api, setParams]);

  useEffect(() => {
    void load(initial);
    // The URL seed is intentionally read only on mount; later loads are user-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="space-y-4">
      <Card className="gap-3 py-3">
        <CardHeader className="py-0">
          <CardTitle className="text-base">ETF category leaders</CardTitle>
          <CardDescription>
            Compare strategy-matched ETFs using total-return history.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <form className="flex flex-wrap gap-2" onSubmit={(event) => {
            event.preventDefault();
            void load(symbol);
          }}>
            <Input
              value={symbol}
              onChange={(event) => setSymbol(event.target.value)}
              aria-label="category-symbol"
              placeholder="e.g. SPY"
              className="h-9 w-32"
            />
            <Button type="submit" size="sm" className="h-9 gap-1.5" disabled={loading}>
              <Search className="size-4" />Find peers
            </Button>
          </form>
          {status && <p className="mt-3 text-sm text-destructive">{status}</p>}
        </CardContent>
      </Card>

      {loading && (
        <Card><CardContent className="space-y-2 pt-6" data-testid="category-loading">
          {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-10 w-full" />)}
        </CardContent></Card>
      )}

      {result && !loading && (
        <Card data-testid="category-results">
          <CardHeader>
            <CardTitle>{result.category.name}</CardTitle>
            <CardDescription>{result.category.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>ETF</TableHead>
                <TableHead className="text-right">Net assets</TableHead>
                <TableHead className="text-right">Expense</TableHead>
                <TableHead>Leads category</TableHead>
                <TableHead className="text-right">1Y return</TableHead>
                <TableHead className="text-right">3Y CAGR</TableHead>
                <TableHead className="text-right">5Y CAGR</TableHead>
                <TableHead className="text-right">3Y Sharpe</TableHead>
                <TableHead className="text-right">3Y volatility</TableHead>
                <TableHead className="text-right">3Y max drawdown</TableHead>
                <TableHead><span className="sr-only">Actions</span></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {result.members.map((member) => <PeerRow key={member.symbol} result={result} member={member} />)}
              </TableBody>
            </Table>
            <p className="mt-4 text-xs text-muted-foreground">
              {result.selection.method === "net_assets"
                ? `Peers are the largest funds by net assets from ${result.selection.candidate_count} strategy-matched candidates; the searched ETF is always included. `
                : `Using the curated peer fallback while supplementary metadata is populated. `}
              Returns include reinvested distributions. Multi-year returns are annualized.
              Risk metrics use the trailing three years; missing values mean insufficient history.
              Strategy membership comes from the seeded taxonomy or explicit provider fund
              metadata. Supplementary metadata is refreshed separately and no supplementary-provider
              calls occur while loading this page.
            </p>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
